import { useCallback, useMemo } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { AILoader } from 'ai-components-ui-library/components/loading/AILoader';
import { ChatView } from 'breeze-engine-chat-components/ChatView';
import { SectionContext } from 'ai-rendering-library/context/SectionContext';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Raven from 'raven-js';
import { AcademyAssistantIcon } from '../AcademyAssistantIcon';
import { useAutoScroll } from '../../hooks/useAutoScroll';
import { AcademyChatActionsProvider } from '../../context/AcademyChatActionsContext';
import { ChatInputArea } from './ChatInputArea';
import { ChatErrorState } from './ChatErrorState';
import { ACADEMY_BLOCK_RENDERERS } from './AcademyBlockRenderers';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const noop = () => {};
const SECTION_CONTEXT_VALUE = {
  getIsUngated: () => true,
  getHasScope: () => false,
  disableThoughts: true,
  isWaitingForChatResponse: false,
  layoutSize: 'LG',
  onCopilotActionClicked: noop,
  onCopilotActionV2Clicked: noop,
  onLinkActionClicked: noop,
  onTemplateActionClicked: noop,
  onThoughtsClicked: noop
};
export const ChatMainView = ({
  manager,
  trackMessageSent,
  trackEvent,
  onContentCardClick,
  onContentCardImpression,
  onStartNewChat
}) => {
  var _active$messages$leng, _active$messages2;
  const active = manager.active;
  const messages = useMemo(() => {
    var _active$messages;
    return (_active$messages = active === null || active === void 0 ? void 0 : active.messages) !== null && _active$messages !== void 0 ? _active$messages : [];
  }, [active === null || active === void 0 ? void 0 : active.messages]);
  const hasMessages = ((_active$messages$leng = active === null || active === void 0 || (_active$messages2 = active.messages) === null || _active$messages2 === void 0 ? void 0 : _active$messages2.length) !== null && _active$messages$leng !== void 0 ? _active$messages$leng : 0) > 0;
  const {
    scrollRef
  } = useAutoScroll(messages);
  const isLoading = active === null || active.status === 'LOADING';
  const hasError = !isLoading && (active === null || active === void 0 ? void 0 : active.status) === 'ERROR' && (active === null || active === void 0 ? void 0 : active.error);
  const isStreaming = active !== null && (active.status === 'STREAMING' || active.status === 'AWAITING_TOOL' || active.status === 'AWAITING_APPROVAL');
  const visibleMessages = messages.filter(msg => msg.phase !== 'commentary' && msg.blocks.some(block => block.type !== 'TOOL' && block.type !== 'THINKING' && block.type !== 'REDACTED_THINKING') && !(msg.role === 'USER' && msg.parentMessageId) // sometimes Breeze Engine returns assistant responses as USER blocks. Need to review this with the BE team
  );
  const handleSend = useCallback((text, appearsIn = 'text-input') => {
    if (!text.trim()) return;
    trackMessageSent(appearsIn);
    manager.sendMessage(text).catch(Raven.captureException.bind(Raven));
  }, [manager, trackMessageSent]);
  const handleStop = useCallback(() => {
    trackEvent('response-stopped');
    manager.stop().catch(Raven.captureException.bind(Raven));
  }, [manager, trackEvent]);
  const chatActions = useMemo(() => ({
    onSuggestionClick: text => handleSend(text, 'suggestion-chip'),
    isStreaming,
    trackEvent,
    onContentCardClick,
    onContentCardImpression
  }), [handleSend, isStreaming, trackEvent, onContentCardClick, onContentCardImpression]);
  return /*#__PURE__*/_jsx(AcademyChatActionsProvider, {
    value: chatActions,
    children: /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      flex: 1,
      blockSize: "100%",
      backgroundColor: "fill-surface-default",
      children: isLoading ? /*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        justifyContent: "center",
        flex: 1,
        overflow: "hidden",
        children: /*#__PURE__*/_jsx(AILoader, {
          isFinished: false
        })
      }) : hasError ? /*#__PURE__*/_jsx(ChatErrorState, {
        onStartNewChat: onStartNewChat
      }) : !hasMessages ? /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        flex: 1,
        paddingInline: "600",
        paddingBlock: "300",
        children: [/*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          alignItems: "center",
          gap: "200",
          children: [/*#__PURE__*/_jsx(AcademyAssistantIcon, {
            size: 68
          }), /*#__PURE__*/_jsx(Text, {
            typography: "heading-500",
            textAlign: "center",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academy-ai-assistant.chat.emptyState"
            })
          })]
        }), /*#__PURE__*/_jsx(View, {
          maxInlineSize: "800px",
          inlineSize: "100%",
          paddingBlockStart: "400",
          children: /*#__PURE__*/_jsx(ChatInputArea, {
            autoFocus: true,
            isStreaming: isStreaming,
            onSend: handleSend,
            onStop: handleStop
          })
        })]
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(View, {
          flex: 1,
          overflow: "auto",
          paddingInline: "600",
          paddingBlock: "300",
          ref: scrollRef,
          children: /*#__PURE__*/_jsxs(View, {
            maxInlineSize: "800px",
            inlineSize: "100%",
            marginInline: "auto",
            children: [/*#__PURE__*/_jsx(SectionContext.Provider, {
              value: SECTION_CONTEXT_VALUE,
              children: /*#__PURE__*/_jsx(ChatView, {
                messages: visibleMessages,
                components: ACADEMY_BLOCK_RENDERERS
              })
            }), isStreaming && /*#__PURE__*/_jsx(Flex, {
              paddingBlockStart: "400",
              children: /*#__PURE__*/_jsx(AILoader, {
                isFinished: false
              })
            })]
          })
        }), /*#__PURE__*/_jsx(View, {
          paddingInline: "600",
          paddingBlock: "300",
          borderBlockStartWidth: "100",
          borderColor: "border-core-default",
          children: /*#__PURE__*/_jsx(View, {
            maxInlineSize: "800px",
            inlineSize: "100%",
            marginInline: "auto",
            children: /*#__PURE__*/_jsx(ChatInputArea, {
              isStreaming: isStreaming,
              onSend: handleSend,
              onStop: handleStop
            })
          })
        })]
      })
    })
  });
};